import { isValidEmail } from 'src/utils/device';
import { securityErrorMessage } from 'src/utils/securityError';

/**
 * Client-side logic for the email (account) login flow on the Auth screen — the buyer-side
 * mirror of the sales cabinet's email-login view. Kept pure and separate from the component so
 * it can be unit-tested in the node env (the app has no component-test setup; see emailChange.ts).
 */

export type LoginStep = 'email' | 'code';

export type LoginEmailCheck =
  | { ok: true; email: string }
  | { ok: false; reason: 'empty' | 'invalid' };

/**
 * Guard the email input before spending an OTP round-trip: trims and lower-cases, then rejects a
 * blank or malformed address. Normalizing here keeps the "we emailed a code to X" copy honest
 * (the api lower-cases too). The server still re-validates; this only gives instant feedback.
 */
export const checkLoginEmail = (input: string): LoginEmailCheck => {
  const email = input.trim().toLowerCase();
  if (!email) return { ok: false, reason: 'empty' };
  if (!isValidEmail(email)) return { ok: false, reason: 'invalid' };
  return { ok: true, email };
};

type LoginEmailFailReason = Extract<LoginEmailCheck, { ok: false }>['reason'];

const LOGIN_EMAIL_ERROR_COPY: Record<LoginEmailFailReason, string> = {
  empty: 'Enter your email address.',
  invalid: 'Enter a valid email address.',
};

/** Buyer-facing copy for a failed email guard. */
export const loginEmailErrorCopy = (reason: LoginEmailFailReason): string =>
  LOGIN_EMAIL_ERROR_COPY[reason];

/** True when the OTP field holds something submittable (non-blank after trim). */
export const isLoginCodeReady = (code: string): boolean => code.trim().length > 0;

/**
 * The code as the api compares it: trimmed and in capitals. The api issues four capital letters
 * and digits and matches them exactly, while a phone keyboard offers lowercase first — so `5yau`
 * typed on an iPhone is the code `5YAU`, not a wrong attempt that burns one of the five tries.
 */
export const normalizeLoginCode = (code: string): string => code.trim().toUpperCase();

/**
 * Human message for a login-step failure. A refused code goes through the shared OTP copy, which
 * reads `attemptsLeft` off the refusal (BUG-13: the api said "4 attempts left" in the body and the
 * screen printed only "Code is not valid"); anything else is the api message when present, else
 * `fallback`. No special "email taken" case — login neither creates nor renames an account.
 */
export const describeLoginError = (err: unknown, fallback: string): string =>
  securityErrorMessage(err, fallback);
